import React, { useEffect, useState } from 'react';
import { diagnosisListPageText } from 'constants/diagnosisListPageConstants';
import { PageTitle, RowDivider } from 'styles/Global/commonStyles';
import moment from 'moment';
import useToggle from 'hooks/CustomHooks/useToggle';
import { useLoginStore } from 'store/useLoginDataStore';
import { useToastStore } from 'store/useToastStore';
import { HospitalPageLabel, FilterWrapper, DiagnosisLayout } from 'styles/Global/hospitalStyles';
import AdminDiagnosisFilter from './AdminDiagnosisFilter';
import { diagnosisPaginationSorting } from 'functions/paginationSorting';
import usePaginationStateHooks from 'hooks/CustomHooks/usePaginationStateHooks';
import AdminDiagnosisListPagination from './AdminDiagnosisListPagination';
import adminDiagnosisPaginationSearchFilter from 'functions/adminDiagnosisPaginationSearchFilter';
import { BorderColor } from 'styles/Global/colorSemantic';
import { AdminInnerLayout } from 'styles/Global/Admin/adminCommonStyles';
import { useLoadingStore } from 'store/useLoadingStore';
import { IDDiagnosis } from 'models/DCK/basicInterface';
import { DCKDiagnosisApi } from 'api/DCK/DCKDiagnosisApi';
import * as XLSX from 'xlsx';
import { calculateAge } from 'utils/calculateAge';
import { changeMobileForm } from 'utils/changeMobileForm';
import { formattedTime } from 'utils/calculateTime';
import { DiagnosisStatus } from 'utils/statusCode';

const AdminDiagnosisListPage = () => {
    const { setLoading } = useLoadingStore();

    const [afterSelectedDate, setAfterSelectedDate] = useState<string | null>(moment().format('YYYY/MM/DD'));
    const [beforeSelectedDate, setBeforeSelectedDate] = useState<string | null>(moment().subtract(1, 'months').format('YYYY/MM/DD'));
    const [initData, setInitData] = useState<IDDiagnosis[]>();
    const [filterData, setFilterData] = useState<IDDiagnosis[] | undefined>(initData);

    const [selectedPeriod, setSelectedPeriod] = useState<number>(1);
    const [selectedSearchFilter, setselectedSearchFilter] = useState<number>(0);
    const [searchValue, setSearchValue] = useState<string>('');
    const [selectSequence, setSelectSequence] = useState<string>('');
    const [sortField, setSortField] = useState<string>('');
    const [sortDirections, setSortDirections] = useState<Record<string, boolean>>({ patientName: false, patientIdentityNumber: false, diagnosisDateTime: false });
    const [afterCalenderDisabled, setAfterCalenderDsiabled] = useState(true);
    const [beforeCalendarInputWarning, setBeforeCalendarInputWarning] = useState(false);
    const [afterCalendarInputWarning, setAfterCalendarInputWarning] = useState(false);

    const [onBeforeCalendar, setOnBeforeCalendar] = useToggle();
    const [onAfterCalendar, setOnAfterCalendar] = useToggle();
    const [onSelectedPeriodDropdown, setOnSelectedPeriodDropdown] = useToggle();
    const [onSearchFilterDropdown, setOnSearchFilterDropdown] = useToggle();

    const { toastToggle, comment, setToast } = useToastStore();
    const { loginData } = useLoginStore();

    const { totalPages, setPage, currentPage, pageGroup, handlePageGroup, currentData } = usePaginationStateHooks(filterData || []);

    // period filter
    useEffect(() => {
        switch (selectedPeriod) {
            case 0:
                setAfterSelectedDate(moment().format('YYYY/MM/DD'));
                setBeforeSelectedDate(moment().subtract(1, 'years').format('YYYY/MM/DD'));
                if (onBeforeCalendar) setOnBeforeCalendar();
                else if (onAfterCalendar) setOnAfterCalendar();
                break;
            case 1:
                setAfterSelectedDate(moment().format('YYYY/MM/DD'));
                setBeforeSelectedDate(moment().subtract(1, 'months').format('YYYY/MM/DD'));
                if (onBeforeCalendar) setOnBeforeCalendar();
                else if (onAfterCalendar) setOnAfterCalendar();
                break;
            case 2:
                setAfterSelectedDate(moment().format('YYYY/MM/DD'));
                setBeforeSelectedDate(moment().subtract(1, 'weeks').format('YYYY/MM/DD'));
                if (onBeforeCalendar) setOnBeforeCalendar();
                else if (onAfterCalendar) setOnAfterCalendar();
                break;
            case 3:
                setAfterSelectedDate(moment().format('YYYY/MM/DD'));
                setBeforeSelectedDate(moment().format('YYYY/MM/DD'));
                if (onBeforeCalendar) setOnBeforeCalendar();
                else if (onAfterCalendar) setOnAfterCalendar();
                break;
            case 4:
                setAfterSelectedDate('');
                setBeforeSelectedDate('');
                setOnBeforeCalendar();
                break;
            default:
                break;
        }
        setBeforeCalendarInputWarning(false);
        setAfterCalendarInputWarning(false);
    }, [selectedPeriod]);

    // 진료목록에서 before date를 넣어야 after date를 선택할 수 있도록 설정
    useEffect(() => {
        if (selectedPeriod === 4 && beforeSelectedDate) {
            setAfterCalenderDsiabled(false);
        }
    }, [beforeSelectedDate]);

    // Data Fetching
    useEffect(() => {
        const beforeDate = moment(beforeSelectedDate, 'YYYY/MM/DD');
        const afterDate = moment(afterSelectedDate, 'YYYY/MM/DD');
        const diff = afterDate.diff(beforeDate, 'days');
        const getDiagnosisList = async () => {
            setLoading(true);
            await DCKDiagnosisApi.getDiagnosisList(`${moment(beforeSelectedDate, 'YYYY/MM/DD').format('YYYYMMDD')}/${moment(afterSelectedDate, 'YYYY/MM/DD').format('YYYYMMDD')}`, loginData.token)
                .then((res) => {
                    console.log(res);
                    const initData =
                        res.data &&
                        res.data
                            .filter((item) => item.status !== -1)
                            .sort((a, b) => {
                                return b.diagnosisDateTime.localeCompare(a.diagnosisDateTime);
                            });
                    setSortField('diagnosisDateTime');
                    setSortDirections((prev) => ({
                        ...Object.keys(prev).reduce((acc, key) => ({ ...acc, [key]: false }), {}),
                        diagnosisDateTime: true,
                    }));
                    setInitData(initData);
                    setFilterData(initData);
                })
                .catch((err) => {
                    console.log(err);
                });
            setLoading(false);
        };

        if (selectedPeriod === 4) {
            if (beforeSelectedDate && onBeforeCalendar && !afterSelectedDate) {
                setOnBeforeCalendar();
                setOnAfterCalendar();
            } else if (beforeSelectedDate && onBeforeCalendar && afterSelectedDate) {
                setOnBeforeCalendar();
            } else if (afterSelectedDate && onAfterCalendar && !beforeSelectedDate) {
                setOnBeforeCalendar();
                setOnAfterCalendar();
            } else if (afterSelectedDate && onAfterCalendar && beforeSelectedDate) {
                setOnAfterCalendar();
            }

            if (diff >= 0 && diff <= 365) {
                getDiagnosisList();
            } else if (diff < 0 || diff > 365) {
                if (selectSequence === 'before') {
                    setAfterSelectedDate(null);
                    setOnAfterCalendar();
                } else if (selectSequence === 'after') {
                    setBeforeSelectedDate(null);
                    setOnBeforeCalendar();
                }
                setToast('최대 1년 내에서만 검색이 가능합니다.');
                setSelectSequence('');
            }
        } else {
            getDiagnosisList();
        }
    }, [beforeSelectedDate, afterSelectedDate, loginData.token]);

    // handlePeriodFilter Dropdown
    const handleDropdown = (e: React.MouseEvent, idx: number, type: string) => {
        e.stopPropagation();
        if (type === 'period') {
            setOnSelectedPeriodDropdown();
            setSelectedPeriod(idx);
        } else {
            setOnSearchFilterDropdown();
            setselectedSearchFilter(idx);
        }
        setSearchValue('');
        setPage(1);
    };

    // 기본 input 클릭 시 calendar dropdown
    const handleCalendarDropdown = (type: string) => {
        if (type === 'before' && onAfterCalendar) {
            setOnBeforeCalendar();
            setOnAfterCalendar();
        } else if (type === 'before' && !onAfterCalendar) {
            setOnBeforeCalendar();
        } else if (type === 'after' && onBeforeCalendar) {
            setOnBeforeCalendar();
            setOnAfterCalendar();
        } else if (type === 'after' && !onBeforeCalendar) {
            setOnAfterCalendar();
        }
    };

    // SearchFilter function
    const handleSearchFilter = () => {
        adminDiagnosisPaginationSearchFilter(searchValue, setSortDirections, setSortField, selectedSearchFilter, initData, setPage, handlePageGroup, setFilterData);
    };

    // reset Button function
    const handleResetButton = () => {
        setSearchValue('');
        setFilterData(initData);
        setSelectedPeriod(1);
        setPage(1);
        handlePageGroup(0);
        setSortDirections((prev) => ({
            ...Object.keys(prev).reduce((acc, key) => ({ ...acc, [key]: false }), {}),
            diagnosisDateTime: true,
        }));
        setSortField('diagnosisDateTime');
    };

    // sorting function

    const handleSorting = (type: string) => {
        const direction = !sortDirections[type];
        console.log(type, direction);
        setSortField(type);
        setSortDirections((prev) => ({
            ...Object.keys(prev).reduce((acc, key) => ({ ...acc, [key]: false }), {}),
            [type]: direction,
        }));
        setFilterData(diagnosisPaginationSorting(type, filterData || [], direction));
    };

    // 엑셀 다운로드 함수
    const handleExcelDownload = () => {
        if (!filterData || filterData.length === 0) {
            setToast('다운로드할 데이터가 없습니다.');
            return;
        }

        try {
            // 상태 코드를 텍스트로 변환하는 함수
            const getStatusText = (status: DiagnosisStatus): string => {
                switch (status) {
                    case DiagnosisStatus.delete:
                        return '삭제됨';
                    case DiagnosisStatus.notAssigned:
                        return '미배정';
                    case DiagnosisStatus.pending:
                        return '대기';
                    case DiagnosisStatus.inProgress:
                        return '진행중';
                    case DiagnosisStatus.completed:
                        return '완료';
                    case DiagnosisStatus.counselReserved:
                        return '상담예약';
                    case DiagnosisStatus.counselComplete:
                        return '상담완료';
                    default:
                        return '알 수 없음';
                }
            };

            // 엑셀 데이터 변환
            const excelData = filterData.map((item, index) => {
                const { birthDate } = calculateAge(item.patientIdentityNumber);
                return {
                    'No.': index + 1,
                    '검사 날짜': formattedTime(item.diagnosisDateTime),
                    '대상자 이름': item.patientName || '',
                    '성별': item.patientGender === 'male' ? '남' : '여',
                    '전화번호': changeMobileForm(item.patientMobile),
                    '대상자 생년월일': moment(birthDate, 'YYYYMMDD').format('YYYY-MM-DD'),
                    '학령': item.gradeName || '',
                    '검사 진행 상태': getStatusText(item.status),
                    '비고': '',
                };
            });

            // 워크북 생성
            const workbook = XLSX.utils.book_new();
            const worksheet = XLSX.utils.json_to_sheet(excelData);

            // 열 너비 설정
            const columnWidths = [
                { wch: 8 },   // 번호
                { wch: 20 },  // 검사 날짜
                { wch: 12 },  // 대상자 이름
                { wch: 8 },   // 성별
                { wch: 15 },  // 전화번호
                { wch: 12 },  // 대상자 생년월일
                { wch: 15 },  // 학령
                { wch: 12 },  // 검사 진행 상태
                { wch: 30 },  // 비고
            ];
            worksheet['!cols'] = columnWidths;

            // 워크시트를 워크북에 추가
            XLSX.utils.book_append_sheet(workbook, worksheet, '진료목록');

            // 파일 다운로드
            const fileName = `진료목록_${moment().format('YYYYMMDD_HHmmss')}.xlsx`;
            XLSX.writeFile(workbook, fileName);

            setToast('엑셀 다운로드가 완료되었습니다.');
        } catch (error) {
            console.error('Excel download failed:', error);
            setToast('엑셀 다운로드 중 오류가 발생했습니다.');
        }
    };

    return filterData ? (
        <DiagnosisLayout>
            <AdminInnerLayout>
                <HospitalPageLabel onClick={() => window.location.reload()}>{diagnosisListPageText.pageLabel}</HospitalPageLabel>
                <PageTitle $marginbottom='24px'>{diagnosisListPageText.pageTitle}</PageTitle>
                <RowDivider $backgroundcolor={BorderColor.color_border_neutral} $marginbottom='16px' />
                <FilterWrapper>
                    <AdminDiagnosisFilter
                        diagnosisNumber={filterData?.length}
                        afterSelectedDate={afterSelectedDate}
                        setAfterSelectedDate={setAfterSelectedDate}
                        beforeSelectedDate={beforeSelectedDate}
                        setBeforeSelectedDate={setBeforeSelectedDate}
                        handleCalendarDropdown={handleCalendarDropdown}
                        onBeforeCalendar={onBeforeCalendar}
                        setOnBeforeCalendar={setOnBeforeCalendar}
                        onAfterCalendar={onAfterCalendar}
                        setOnAfterCalendar={setOnAfterCalendar}
                        selectedPeriod={selectedPeriod}
                        selectedSearchFilter={selectedSearchFilter}
                        handleDropdown={handleDropdown}
                        setOnSelectedPeriodDropdown={setOnSelectedPeriodDropdown}
                        onSelectedPeriodDropdown={onSelectedPeriodDropdown}
                        setOnSearchFilterDropdown={setOnSearchFilterDropdown}
                        onSearchFilterDropdown={onSearchFilterDropdown}
                        maxDate={afterSelectedDate}
                        minDate={beforeSelectedDate}
                        onChange={setSearchValue}
                        searchValue={searchValue}
                        handleSearchFilter={handleSearchFilter}
                        setSelectSequence={setSelectSequence}
                        toastToggle={toastToggle}
                        comment={comment}
                        afterCalenderDisabled={afterCalenderDisabled}
                        setBeforeCalendarInputWarning={setBeforeCalendarInputWarning}
                        setAfterCalendarInputWarning={setAfterCalendarInputWarning}
                        beforeCalendarInputWarning={beforeCalendarInputWarning}
                        afterCalendarInputWarning={afterCalendarInputWarning}
                        handleExcelDownload={handleExcelDownload}
                    />
                </FilterWrapper>
                <AdminDiagnosisListPagination
                    initNumber={initData?.length}
                    currentPage={currentPage}
                    totalPages={totalPages}
                    pageGroup={pageGroup}
                    handlePageGroup={handlePageGroup}
                    handlePageChange={setPage}
                    currentData={currentData}
                    handleSorting={handleSorting}
                    sortField={sortField}
                    sortDirections={sortDirections}
                    handleResetButton={handleResetButton}
                />
            </AdminInnerLayout>
        </DiagnosisLayout>
    ) : null;
};

export default AdminDiagnosisListPage;
